import { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { CompanyMeasurementDateName } from 'api';
import { CompanyMeasurementDateNameNullable } from 'api/models/CompanyMeasurementDateNameNullable';
import { MEASUREMENT_DATE_ACTION_ADD } from 'common/constants/measurement-date';
import { MeasurementDateAction, SelectedMeasurementDate } from 'common/types/measurementDate';
import { LayoutContextValues } from 'common/types/store';
import { LayoutContext } from 'context';
import { FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE } from 'pages/Funds/common/constants';
import { MeasurementDateModal } from 'pages/Funds/components';
import { useGetMeasurementDates, useSetFilters, useSetInitialMeasurementDate } from 'pages/Funds/hooks';
import { checkEmptyObject, extractDate, getArrayValue, getObjectValue } from 'utilities';
import { UseFundMeasurementDateFilter } from './types';

const useFundMeasurementDateFilter: UseFundMeasurementDateFilter = params => {
  const { activeFund } = params;

  const [openMeasurementDatesDialog, setOpenMeasurementDatesDialog] = useState(false);
  const [mdHasBeenDeleted, setMdHasBeenDeleted] = useState(false);
  // Initialize with "Most Recent" as the default selection
  const [selectedMeasurementDate, setSelectedMeasurementDate] = useState<SelectedMeasurementDate>(FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE);
  const [measurementDateAction, setMeasurementDateAction] =
    useState<MeasurementDateAction>(MEASUREMENT_DATE_ACTION_ADD);

  const { setCurrentMeasurementDate, setPageFilters } = useContext(LayoutContext) as unknown as LayoutContextValues;

  const location = useLocation();
  const parsedQuery = queryString.parse(location.search);
  const { date: dateQueryString } = parsedQuery;

  const { setFilters } = useSetFilters();

  const { measurementDates, getMeasurementDates } = useGetMeasurementDates({
    activeFund,
    setSelectedMeasurementDate,
    openMeasurementDatesDialog,
  });

  // Include "Most Recent" in the measurement dates list
  const companyMeasurementDatesByFund = useMemo<(CompanyMeasurementDateName | CompanyMeasurementDateNameNullable)[]>(
    () => [FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE, ...getArrayValue(measurementDates) as CompanyMeasurementDateName[]],
  [measurementDates]
  );

  // Check if the selected measurement date is "Most Recent"
  const isMostRecentMeasurementDate = useMemo<boolean>(
    () => selectedMeasurementDate?.name === FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE.name,
    [selectedMeasurementDate]
  );

  const openedMeasurementDate = useMemo(
    () => getObjectValue(measurementDates?.find(measurementDate => measurementDate.is_open)),
    [measurementDates]
  );

  // Normalize date query string to string | null
  const dateQueryStringValue = useMemo(
    () => (typeof dateQueryString === 'string' ? dateQueryString : null),
    [dateQueryString]
  );

  const { setInitialMeasurementDate } = useSetInitialMeasurementDate({
    dateQueryString: dateQueryStringValue,
    measurementDates: companyMeasurementDatesByFund,
    openedMeasurementDate,
    setSelectedMeasurementDate,
  });

  const activeMeasurementDate = useMemo<CompanyMeasurementDateName | CompanyMeasurementDateNameNullable | Record<string, never>>(() => {
    // If "Most Recent" is selected, return it as is
    if (isMostRecentMeasurementDate) {
      return FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE;
    }

    if (selectedMeasurementDate) {
      const selectedMD = getObjectValue(
        measurementDates?.find(measurementDate => measurementDate.date === selectedMeasurementDate.date)
      );

      const infoMD = checkEmptyObject(selectedMD) ?? getObjectValue(checkEmptyObject(openedMeasurementDate));

      return infoMD;
    }

    return {};
  }, [measurementDates, openedMeasurementDate, selectedMeasurementDate, isMostRecentMeasurementDate]);

  const openMeasurementDateModal = (action: MeasurementDateAction = MEASUREMENT_DATE_ACTION_ADD) => {
    let dialogAction = action;
    if (!action || typeof action !== 'string') {
      dialogAction = MEASUREMENT_DATE_ACTION_ADD;
    }
    setOpenMeasurementDatesDialog(true);
    setMeasurementDateAction(dialogAction);
  };

  const closeMeasurementDateModal = () => setOpenMeasurementDatesDialog(false);

  const MemoizedMeasurementDateModal = useMemo(
    () => (
      <MeasurementDateModal
        activeFund={activeFund}
        closeMeasurementDateModal={closeMeasurementDateModal}
        getFundMeasurementDates={getMeasurementDates}
        openMeasurementDatesDialog={openMeasurementDatesDialog}
        measurementDateAction={measurementDateAction}
        setMdHasBeenDeleted={setMdHasBeenDeleted}
        selectedFmdId={selectedMeasurementDate?.fmd_id}
      />
    ),
    [activeFund, measurementDateAction, getMeasurementDates, openMeasurementDatesDialog, selectedMeasurementDate]
  );

  // Extract date from date query string (now normalized to string | null)
  const dateSlugParam = useMemo(
    () => (dateQueryStringValue ? extractDate({ dateSlug: dateQueryStringValue }) : undefined),
    [dateQueryStringValue]
  );

  // Set Page Filters
  useEffect(() => {
    if (!isEmpty(companyMeasurementDatesByFund) && selectedMeasurementDate) {
      setFilters({
        measurementDates: companyMeasurementDatesByFund,
        openedMeasurementDate,
        openMeasurementDateModal,
        selectedMeasurementDate,
        setSelectedMeasurementDate,
      });
    } else if (isEmpty(measurementDates) && mdHasBeenDeleted) {
      setPageFilters?.(null);
    }

    return () => {
      setPageFilters?.(null);
    };
  }, [
    companyMeasurementDatesByFund,
    openedMeasurementDate,
    selectedMeasurementDate,
    setFilters,
    setPageFilters,
    mdHasBeenDeleted,
    measurementDates,
  ]);

  // Set initial Measurement Date to "Most Recent" when there is no date query string
  useEffect(() => {
    if (isNil(dateQueryStringValue)) {
      // If date query string is null/undefined, set to "Most Recent"
      // Also check if current selection is not "Most Recent" to avoid unnecessary updates
      if (selectedMeasurementDate?.name !== FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE.name) {
        setSelectedMeasurementDate(FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE);
      }
    } else if (
      !isEmpty(companyMeasurementDatesByFund) &&
      (dateSlugParam !== selectedMeasurementDate?.date) &&
      // Don't override if "Most Recent" is explicitly selected
      selectedMeasurementDate?.name !== FUND_SUMMARY_MOST_RECENT_MEASUREMENT_DATE.name
    ) {
      setInitialMeasurementDate();
    }
  }, [dateSlugParam, dateQueryStringValue, companyMeasurementDatesByFund, selectedMeasurementDate, setInitialMeasurementDate]);

  // Update the current measurement date in the layout context
  useEffect(() => {
    if (setCurrentMeasurementDate) {
      setCurrentMeasurementDate(activeMeasurementDate);
    }
  }, [activeMeasurementDate, setCurrentMeasurementDate]);

  return {
    activeMeasurementDate,
    MeasurementDateModal: () => MemoizedMeasurementDateModal,
    measurementDates: companyMeasurementDatesByFund,
    openMeasurementDateModal,
  };
};

export default useFundMeasurementDateFilter;
